import React from 'react';
import styled, { css } from 'styled-components';
import { FaChevronLeft, FaChevronRight, FaCheck } from 'react-icons/fa';
import { LuLockKeyhole } from 'react-icons/lu';
import theme from '../../assets/css/theme';
import media from '../../assets/css/media';
import { MOBILE_TEMPLATE_WIDTH, useTemplatesList } from './hooks/useTemplatesList';
import { getTemplateTitle } from '../../modules/selectors/templates';

const Wrapper = styled.div<{ $newDesign?: boolean }>`
  position: relative;
  display: flex;
  align-items: ${(props) => (props.$newDesign ? 'flex-start' : 'center')};
`;

const ScrollContainer = styled.div`
  display: flex;
  overflow-x: auto;
  scroll-behavior: smooth;
  padding: 10px;
  flex: 1;
  cursor: grab;

  /* Hide scrollbar */
  -ms-overflow-style: none;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const ArrowButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.7;
  }
`;

/** Centers the arrow on the 3:2 thumbnail (container padding + item margin = 15px), not on thumb + name. */
const NewDesignArrowButton = styled(ArrowButton)`
  margin-top: 15px;
  height: 100px;

  @media (max-width: ${media.mobile}) {
    height: ${(MOBILE_TEMPLATE_WIDTH * 2) / 3}px;
  }
`;

const TemplateImage = styled.img<{ $newDesign?: boolean }>`
  width: 100%;
  ${(props) =>
    props.$newDesign &&
    `display: block;
    height: 100%;
    object-fit: cover;`}
  -webkit-user-drag: none;
  -khtml-user-drag: none;
  -moz-user-drag: none;
  -o-user-drag: none;
  user-drag: none;
`;

interface ITemplateItem {
  isSelected: boolean;
  isBlocked: boolean;
}

const blockedCss = css`
  ${TemplateImage} {
    &:hover {
      cursor: not-allowed;
    }
  }
`;
const TemplateItem = styled.div<ITemplateItem & { $newDesign?: boolean }>`
  width: 150px;
  flex: 0 0 auto;
  margin: 5px 5px 5px 5px;
  position: relative;
  cursor: pointer;
  ${(props) =>
    props.$newDesign
      ? ''
      : `overflow: hidden;
        box-shadow: ${props.isSelected ? '0px 0px 15px -2px rgb(92, 135, 98)' : 'none'};`}
  ${(props) => props.isBlocked && blockedCss};

  @media (max-width: ${media.mobile}) {
    ${(props) => props.$newDesign && `width: ${MOBILE_TEMPLATE_WIDTH}px;`}
  }
`;

const TemplateThumb = styled.div<{ isSelected: boolean }>`
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  border: 1px solid
    ${(props) => (props.isSelected ? theme.commonColors.second : theme.formsColors.mainBorderColor)};
  box-shadow: ${(props) => (props.isSelected ? `0 0 0 1px ${theme.commonColors.second}` : 'none')};
  aspect-ratio: 3 / 2;
  background: ${theme.commonColors.mainBackground};
`;

const CheckBadge = styled.span`
  position: absolute;
  top: 6px;
  right: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${theme.commonColors.second};
  color: #fff;
`;

/** Dims a paid template the photographer hasn't bought, keeping the card visible underneath. */
const LockedOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
`;

const TemplateName = styled.div`
  margin-top: 8px;
  text-align: left;
  font-size: ${theme.fontSizes.small};
  font-weight: 500;
  color: ${theme.commonColors.main};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

type Props = {
  values: {
    selectedTemplateId: string;
  };
  setValue: (values: Partial<Props['values']>) => void;
  /** Renders the redesigned card: framed thumb, check badge and name label. */
  newDesign?: boolean;
};

export const TemplatesList = ({ values, setValue, newDesign }: Props) => {
  const { templates, scrollRef, isSelected, isLocked, selectTemplate, scroll, dragHandlers } =
    useTemplatesList({
      selectedTemplateId: values.selectedTemplateId,
      onSelect: (selectedTemplateId) => setValue({ selectedTemplateId })
    });

  const Arrow = newDesign ? NewDesignArrowButton : ArrowButton;

  return (
    <Wrapper $newDesign={newDesign}>
      <Arrow onClick={() => scroll('left')}>
        <FaChevronLeft size={15} color="gray" />
      </Arrow>
      <ScrollContainer ref={scrollRef} {...dragHandlers}>
        {templates.map((template) => (
          <TemplateItem
            key={template._id}
            isSelected={isSelected(template._id)}
            isBlocked={false}
            $newDesign={newDesign}
            onClick={() => selectTemplate(template._id)}
            data-cy={`template-item-${template._id}`}
          >
            {newDesign ? (
              <>
                <TemplateThumb isSelected={isSelected(template._id)}>
                  <TemplateImage
                    $newDesign
                    src={template.newCardUrl || template.cardUrl}
                    alt="TemplateImage"
                  />
                  {isLocked(template) && (
                    <LockedOverlay data-cy={`template-locked-${template._id}`}>
                      <LuLockKeyhole size={24} strokeWidth={1.75} />
                    </LockedOverlay>
                  )}
                  {isSelected(template._id) && (
                    <CheckBadge>
                      <FaCheck size={10} />
                    </CheckBadge>
                  )}
                </TemplateThumb>
                <TemplateName>{getTemplateTitle(template, true)}</TemplateName>
              </>
            ) : (
              <TemplateImage src={template.cardUrl} alt="TemplateImage" />
            )}
          </TemplateItem>
        ))}
      </ScrollContainer>
      <Arrow onClick={() => scroll('right')}>
        <FaChevronRight size={15} color="gray" />
      </Arrow>
    </Wrapper>
  );
};
export default TemplatesList;
